import { useEffect, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Coins, Minus, Plus, X } from 'lucide-react'
import { LIVE_BET_DELAY_MS, legKey, type Slip, type SlipType, type StockbetQuotesResponse } from '@brochure/shared'
import { SideMark } from '@/components/stoccopong/SideMark'
import { BottomSheet, sheetButtonClass } from '@/components/ui/BottomSheet'
import { ApiError, errorMessage } from '@/lib/api'
import {
  acceptOdds, allowedTypes, removeClosed, removeSelection, setType, slipQuoteState, slipSummary, toggleBanker, toggleSystemSize,
  type DraftSlip, type SlipLimitsView,
} from '@/lib/slip'
import { formatOdds } from '@/lib/stockbet'
import { cn } from '@/lib/utils'
import { useOutcomeLabel } from './useOutcomeLabel'

const STEP = 5
const SHORTCUTS = [1, 5, 10, 25] as const
const TYPES: SlipType[] = ['single', 'multiple', 'system']

type SlipSheetProps = {
  /** Invia la bozza (`useSlip().place`): risolve con la schedina giocata, `open` o `pending` se c'è una partita in corso. */
  place: (draft: DraftSlip) => Promise<Slip>
  /** Una POST della schedina è in volo (da questa o da un'altra pagina, `useSlip().isPlacing`): "Gioca" spento. */
  isPlacing: boolean
  slip: DraftSlip
  quotes: StockbetQuotesResponse | undefined
  /** Errore nel caricare le quote della schedina; `onRetryQuotes` le richiede di nuovo. */
  quotesError?: unknown
  onRetryQuotes: () => void
  limits: SlipLimitsView
  onChange: (slip: DraftSlip) => void
  onClose: () => void
  onPlaced: (slip: Slip) => void
  /** Dopo un 409: rilegge le quote, così la schedina mostra le nuove e gli esiti chiusi. */
  onStale: () => void
}

/**
 * La schedina nel `BottomSheet` (spec 4): tipo, eventi con le quote (vecchia → nuova se cambiate, "Rimuovi" se chiusi),
 * fisse e ordini del sistema, puntata per colonna, riepilogo con bonus e vincita. L'attesa live la mostra `LiveSlips`.
 */
export function SlipSheet({ place: placeSlip, isPlacing, slip, quotes, quotesError, onRetryQuotes, limits, onChange, onClose, onPlaced, onStale }: SlipSheetProps) {
  const { t, i18n } = useTranslation()
  const label = useOutcomeLabel()
  const [error, setError] = useState<string | null>(null)

  const stake = slip.stakePerColumn
  const setStake = (value: number) => onChange({ ...slip, stakePerColumn: Math.max(1, Math.round(value)) })
  // Il campo ha il suo testo: mentre si digita può restare vuoto (la schedina tiene l'ultima puntata valida e Gioca resta spento),
  // a blur torna a un intero ≥ 1. I pulsanti e le scorciatoie cambiano la schedina e il testo li segue.
  const [stakeText, setStakeText] = useState(String(stake))
  useEffect(() => setStakeText((text) => (Number(text) === stake && text !== '' ? text : String(stake))), [stake])
  const isStakeValid = stakeText !== '' && Number(stakeText) === stake

  const state = slipQuoteState(slip, quotes)
  const summary = slipSummary(slip, limits)
  const allowed = allowedTypes(slip)
  const free = slip.legs.filter((l) => !l.banker).length
  const isLive = slip.legs.some((l) => quotes?.[legKey(l)]?.status === 'live')
  const hasClosed = state.closed.length > 0
  const hasChanged = state.changed.length > 0
  // Finché le quote delle partite in schedina non arrivano, le quote accettate potrebbero essere vecchie: niente giocata.
  const isQuoting = quotes === undefined && slip.legs.length > 0
  const canSubmit = !isPlacing && !isQuoting && isStakeValid && !hasClosed && summary.columns > 0 && summary.warnings.length === 0
  const isClosed = (l: number, s: number) => state.closed.some((c) => c.leg === l && c.selection === s)

  async function place(draft: DraftSlip) {
    setError(null)
    try {
      onPlaced(await placeSlip(draft))
    } catch (placeError) {
      const stale = placeError instanceof ApiError && (placeError.code === 'oddsChanged' || placeError.code === 'marketClosed')
      setError(stale ? t(`stockbet.slip.errors.${(placeError as ApiError).code}`) : errorMessage(t, placeError))
      if (stale) onStale()
    }
  }

  function submit() {
    if (!hasChanged) return void place(slip)
    const accepted = acceptOdds(slip, state)
    onChange(accepted)
    void place(accepted)
  }

  return (
    <BottomSheet title={t('stockbet.slip.title')} subtitle={t('stockbet.slip.subtitle', { count: slip.legs.length })} onClose={onClose} testId="slip-sheet" docked>
      <div role="radiogroup" aria-label={t('stockbet.slip.typeLabel')} className="grid grid-cols-3 gap-1 rounded-xl bg-paper/10 p-1">
        {TYPES.map((type) => (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={slip.type === type}
            disabled={!allowed.includes(type)}
            data-testid={`slip-type-${type}`}
            onClick={() => onChange(setType(slip, type))}
            className={cn('min-h-11 rounded-lg font-body text-sm font-semibold transition-colors disabled:opacity-35', slip.type === type ? 'bg-paper text-ink' : 'text-paper/80')}
          >
            {t(`stockbet.slip.types.${type}`)}
          </button>
        ))}
      </div>
      {!allowed.includes('multiple') && slip.legs.length >= 2 && <p className="mt-2 font-body text-xs text-paper/60">{t('stockbet.slip.multipleHint')}</p>}

      <ol className="mt-4 space-y-2">
        {slip.legs.map((leg, l) => {
          const names = Object.fromEntries(leg.selections.filter((s) => s.subjectId).map((s) => [s.subjectId!, s.subjectName ?? '']))
          return (
            <li key={legKey(leg)} data-testid={`slip-leg-${legKey(leg)}`} className="rounded-xl border border-paper/15 bg-paper/5 px-3 py-2.5">
              <div className="flex items-center gap-2 font-body text-xs font-semibold text-paper/70">
                {leg.matchId === null ? (
                  <span className="min-w-0 flex-1 truncate">{t('stockbet.outrights.legTitle')}</span>
                ) : (
                  <>
                    <SideMark side="home" className="h-3" />
                    <span className="min-w-0 flex-1 truncate">{leg.home} – {leg.away}</span>
                    <SideMark side="away" className="h-3" />
                  </>
                )}
                {slip.type === 'system' && (
                  <button
                    type="button"
                    aria-pressed={leg.banker}
                    data-testid={`slip-banker-${legKey(leg)}`}
                    onClick={() => onChange(toggleBanker(slip, l))}
                    // Pillola da 32 px, ma l'area di tocco (after:) arriva a 44 px senza alzare la riga.
                    className={cn('relative ml-1 min-h-8 rounded-full border px-2.5 text-[0.7rem] font-bold after:absolute after:-inset-1.5 after:content-[""]', leg.banker ? 'border-sun bg-sun text-ink' : 'border-paper/30 text-paper/70')}
                  >
                    {t('stockbet.slip.banker')}
                  </button>
                )}
              </div>
              <ul className="mt-1.5 space-y-1">
                {leg.selections.map((s, j) => {
                  const now = state.current[l]?.[j]
                  const closed = isClosed(l, j)
                  return (
                    <li key={`${s.market}-${s.selection}-${s.line ?? ''}-${s.subjectId ?? ''}`} className="flex items-center gap-2 font-body text-sm text-paper">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs text-paper/55">
                          {t(`stockbet.marketNames.${s.market}`)}
                          {/* Nei bicchieri del giocatore l'esito è solo "Over 2,5": il nome va accanto al mercato. */}
                          {s.market === 'player_cups' && s.subjectName && ` · ${s.subjectName}`}
                        </span>
                        <span className={cn('block truncate font-semibold', closed && 'line-through opacity-60')}>{label(s, names)}</span>
                      </span>
                      {closed ? (
                        <span className="shrink-0 text-xs font-semibold text-coral">{t('stockbet.slip.closed')}</span>
                      ) : (
                        <span className="shrink-0 tabular-nums font-semibold">
                          {now !== null && now !== undefined && now !== s.odds ? (
                            <>
                              <span className="text-paper/45 line-through">{formatOdds(s.odds, i18n.language)}</span> <span className="text-sun">{formatOdds(now, i18n.language)}</span>
                            </>
                          ) : (
                            formatOdds(s.odds, i18n.language)
                          )}
                        </span>
                      )}
                      <button type="button" aria-label={t('stockbet.slip.remove')} data-testid={`slip-remove-${l}-${j}`} onClick={() => onChange(removeSelection(slip, { leg: l, selection: j }))} className="-my-1 -mr-2 grid size-11 shrink-0 place-items-center rounded-full text-paper/60 hover:bg-paper/10">
                        <X className="size-4" aria-hidden="true" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </li>
          )
        })}
      </ol>
      {hasClosed && (
        <button type="button" data-testid="slip-remove-closed" onClick={() => onChange(removeClosed(slip, state))} className="mt-2 min-h-11 font-body text-sm font-semibold text-coral underline-offset-4 hover:underline">
          {t('stockbet.slip.removeClosed', { count: state.closed.length })}
        </button>
      )}

      {slip.type === 'system' && free > 0 && (
        <div className="mt-4">
          <p className="font-body text-xs font-semibold text-paper/70">{t('stockbet.slip.orders')}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {Array.from({ length: free }, (_, i) => i + 1).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={slip.systemSizes.includes(k)}
                data-testid={`slip-order-${k}`}
                onClick={() => onChange(toggleSystemSize(slip, k))}
                className={cn('min-h-11 min-w-11 rounded-full border px-3 font-body text-sm font-semibold tabular-nums', slip.systemSizes.includes(k) ? 'border-sun bg-sun text-ink' : 'border-paper/30 text-paper')}
              >
                {k}/{free}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-xl border-2 border-ink bg-paper text-ink shadow-[4px_4px_0_var(--color-sun)]" style={{ '--notch-bg': 'var(--color-ink)' } as CSSProperties}>
        <div className="px-4 pt-3 pb-3">
          <label htmlFor="slip-stake" className="font-body text-xs font-semibold text-ink-soft">
            {slip.type === 'system' ? t('stockbet.slip.stakePerColumn') : t('stockbet.slip.stake')}
          </label>
          <div className="mt-1.5 flex items-center gap-2">
            <button type="button" aria-label={t('stockbet.slip.decrease')} disabled={stake <= 1} onClick={() => setStake(stake - STEP)} className="grid size-11 shrink-0 place-items-center rounded-full border-2 border-ink disabled:opacity-30">
              <Minus className="size-4" aria-hidden="true" />
            </button>
            <input id="slip-stake" data-testid="slip-stake" type="number" inputMode="numeric" min={1} step={1} value={stakeText} onChange={(event) => { setStakeText(event.target.value); if (event.target.valueAsNumber >= 1) setStake(event.target.valueAsNumber) }} onBlur={() => setStakeText(String(stake))} className="min-h-11 w-full min-w-0 rounded-lg border-2 border-ink/20 bg-paper-deep/60 px-3 text-center font-display text-2xl tabular-nums focus:border-ink focus:outline-none" />
            <button type="button" aria-label={t('stockbet.slip.increase')} onClick={() => setStake(stake + STEP)} className="grid size-11 shrink-0 place-items-center rounded-full border-2 border-ink">
              <Plus className="size-4" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SHORTCUTS.map((n) => (
              <button key={n} type="button" data-testid={`slip-stake-${n}`} onClick={() => setStake(n)} className={cn('min-h-11 rounded-full border px-4 font-body text-sm font-semibold tabular-nums', stake === n ? 'border-ink bg-ink text-paper' : 'border-ink/25')}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <span aria-hidden="true" className="ticket-perforation block" />
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 px-4 pt-3 pb-4 font-body text-sm">
          {slip.type === 'system' && (
            <>
              <dt className="text-ink-soft">{t('stockbet.slip.columnsLabel')}</dt>
              <dd data-testid="slip-columns" className="text-right tabular-nums">{summary.columns}</dd>
            </>
          )}
          <dt className="text-ink-soft">{t('stockbet.slip.total')}</dt>
          <dd data-testid="slip-total" className="text-right tabular-nums">{summary.totalStake}</dd>
          {summary.bonusPct > 0 && (
            <>
              <dt className="text-ink-soft">{t('stockbet.slip.bonus')}</dt>
              <dd className="text-right tabular-nums">+{summary.bonusPct}%</dd>
            </>
          )}
          <dt className="self-end text-ink-soft">{t('stockbet.slip.potential')}</dt>
          <dd className="flex items-center justify-end gap-1.5 font-display text-2xl tabular-nums">
            <Coins className="size-5 text-sun-ink" aria-hidden="true" />
            <span data-testid="slip-potential">{summary.potential.min === summary.potential.max ? summary.potential.max : `${summary.potential.min}–${summary.potential.max}`}</span>
          </dd>
        </dl>
      </div>

      {summary.warnings.map((w) => (
        <p key={w} role="alert" className="mt-3 rounded-xl border border-coral/40 bg-coral/10 px-4 py-2.5 font-body text-sm text-coral">
          {t(`stockbet.slip.warnings.${w}`, { legs: limits.maxLegs, columns: limits.maxColumns, max: limits.maxBet ?? 0 })}
        </p>
      ))}
      {quotesError !== undefined && quotes === undefined && (
        <div role="alert" className="mt-3 rounded-xl border border-coral/40 bg-coral/10 px-4 py-2.5 font-body text-sm text-coral">
          <p>{t('stockbet.slip.quotesError')}</p>
          <button type="button" data-testid="slip-quotes-retry" onClick={onRetryQuotes} className="mt-1 min-h-11 font-semibold underline underline-offset-4">
            {t('stockbet.slip.retry')}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-xl border border-coral/40 bg-coral/10 px-4 py-2.5 font-body text-sm text-coral">
          {error}
        </p>
      )}
      <button type="button" data-testid={hasChanged ? 'slip-accept' : 'slip-confirm'} disabled={!canSubmit} onClick={submit} className={cn(sheetButtonClass, 'mt-4 bg-sun text-ink hover:bg-sun/90')}>
        {hasChanged ? t('stockbet.slip.acceptAll') : t('stockbet.slip.play', { count: summary.totalStake })}
      </button>
      {isLive && <p className="mt-2 text-center font-body text-xs text-paper/60">{t('stockbet.slip.liveDelay', { count: LIVE_BET_DELAY_MS / 1000 })}</p>}
      <button type="button" data-testid="slip-clear" onClick={() => onChange({ ...slip, legs: [] })} className="mt-1 min-h-11 w-full font-body text-sm text-paper/60 hover:text-paper">
        {t('stockbet.slip.clear')}
      </button>
    </BottomSheet>
  )
}
